import React, { forwardRef, useImperativeHandle } from 'react';
import useMediaPlayer, { formatPlayerTime } from '../../hooks/useMediaPlayer';
import './Viewer.css';

const MediaPlayer = forwardRef(({ src, onTimeUpdate }, ref) => {
  const {
    mediaRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    seekTo,
    play,
    pause,
    togglePlay,
    handleSeek,
    handleVolumeChange,
    handleRateChange,
  } = useMediaPlayer({ onTimeUpdate, enableKeyboard: true });

  useImperativeHandle(ref, () => ({
    seekTo,
    play,
    pause,
  }));

  const isVideo = src?.includes('.mp4') || src?.includes('.mov') || src?.includes('.webm') || src?.includes('.mkv');

  return (
    <div className="media-player">
      {isVideo ? (
        <video
          ref={mediaRef}
          src={src}
          className="media-element video-element"
          playsInline
          preload="metadata"
          onError={(e) => console.error('Video error:', e.target.error)}
        />
      ) : (
        <audio ref={mediaRef} src={src} className="media-element audio-element" />
      )}

      <div className="player-controls">
        <button className="play-button" onClick={togglePlay}>
          {isPlaying ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        <div className="time-display">
          {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
        </div>

        <div className="progress-container" onClick={handleSeek}>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
            />
          </div>
        </div>

        <div className="volume-control">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>

        <div className="rate-control">
          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
            <button
              key={rate}
              className={`rate-button ${playbackRate === rate ? 'active' : ''}`}
              onClick={() => handleRateChange(rate)}
            >
              {rate}x
            </button>
          ))}
        </div>
      </div>

      <p className="player-hint">
        Keyboard: Space=play/pause, Arrows=skip 5s, Up/Down=volume
      </p>
    </div>
  );
});

MediaPlayer.displayName = 'MediaPlayer';

export default MediaPlayer;
